import React, { useCallback, useEffect, useState, useRef } from "react";
import { Route, Link } from "react-router-dom";

import { API, graphqlOperation, Storage } from "aws-amplify";
import { getOutboundShipmentMobile } from "../../../graphql/outbound/custom-queries";
import { updateShipmentLineItemMini } from "../../../graphql/outbound/custom-mutations";
import { performUpdateOutbound } from "../../../graphql/outbound/outboundCRUD";

import { useNotify, useRedirect } from "react-admin";

import {
  TextField,
  CardActions,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  FormControlLabel,
  Switch,
  Box,
  Backdrop,
  CardContent,
} from "@material-ui/core";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import DoneIcon from "@material-ui/icons/Done";
import DoubleCheckIcon from "@material-ui/icons/DoneAll";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";

import {
  ScannerIcon,
  TrainIcon,
  TruckIcon,
} from "../../../components/common/iconography";
import ErrorCard from "../../../components/ErrorCard";
import CrewCard from "../../../components/common/CrewCard";
import { CustomFieldsMobile } from "../../../components/common/CustomFields";
import PhotoManager from "../../../components/common/PhotoManager";
import { accountNameRenderer } from "../../../components/common/AccountNameRenderer";
import { needsOutboundRailcarSpotting } from "../../../components/common/locationPickerTrackUtils";

import {
  CarrierType,
  NoteType,
  OutboundStatus,
  OutboundState,
  ShipmentLineItemStatus,
  UserType,
  MAX_PHOTO_NUM_OUTBOUND,
  AclType,
  PhotoStatus,
  ResourceType,
} from "../../../utils/constants";
import { generateSignatureKey } from "../../../utils/fileUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import {
  defaultNumberFormat,
  getInventoryPrimaryField,
  getLogState,
} from "../../../utils/utils";
import useStickyState from "../../../utils/stickyState";
import { sortShipmentItemByLocation } from "../../../utils/locationUtils";
import { isAuthorized, logError } from "../../../utils/appUtils";
import { applyRejectionRollback } from "../../../utils/shipmentPlacement";
import { useTrackCarSubscriptionRefresh } from "../../../utils/useTrackCarSubscriptionRefresh";
import { SealDialog } from "./Seals/SealsDialog";
import { SignatureDialog } from "../../../managerApp/resources/outboundShipments/SignatureDialog";
import { useOutboundContext } from "./context";
import InventoryItemCard from "./ShipmentItem";

const ProcessOutboundShipmentCard = ({ classes, match, history }) => {
  return (
    <>
      {
        <Route
          path={`${match.path}/inventories/:id`}
          render={(props) => <InventoryItemCard {...props} />}
        />
      }
      {
        <Route
          path={`${match.path}`}
          exact
          render={(props) => (
            <OutboundShipmentCard
              classes={classes}
              history={history}
              {...props}
            />
          )}
        />
      }
    </>
  );
};

export default ProcessOutboundShipmentCard;

function OutboundShipmentCard({ classes, match, history }) {
  const { state, dispatch } = useOutboundContext();
  const { currentShipment, inventoryItems, notes, loading } = state;
  const notify = useNotify();
  const redirect = useRedirect();
  const [invalidIdError, setInvalidIdError] = useState();
  const isComponentMounted = useRef(true);
  const [signatureType, setSignatureType] = useState();
  const [open, setOpen] = useState(false);
  const [carrierSignatureImg, setCarrierSignatureImg] = useState();
  const [yardSignatureImg, setYardSignatureImg] = useState();
  const [photosErrorMsg, setPhotosErrorMsg] = useState("");
  const [photos, setPhotos] = useState([]);
  const signatureRef = useRef(null);
  const [invalidSigMsg, setInvalidSigMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [isSealDialogOpen, setIsSealDialogOpen] = useState(false);
  const [isSealsConfirmationOpen, setIsSealsConfirmationOpen] = useState(false);

  const note = notes[currentShipment.id];

  const getCompletedPhotoCount = () =>
    Array.isArray(photos)
      ? photos.filter((p) => p && p.status !== PhotoStatus.ERROR).length
      : 0;

  const getData = useCallback(
    async ({ showSpinner = true } = {}) => {
      try {
        if (showSpinner) {
          dispatch({ type: "SET_LOADING", payload: true });
        }
        const response = await API.graphql(
          graphqlOperation(getOutboundShipmentMobile, {
            id: match.params.id,
          }),
        );

        if (!response.data.getOutboundShipment) {
          setInvalidIdError(`Outbound shipment was not found.`);
        } else {
          const outboundShipment = response.data.getOutboundShipment;
          if (outboundShipment.status === OutboundStatus.RELEASED) {
            notify(`Outbound shipment was released.`, "warning");
            goToOutbound();
          }
          const inventoryItems = [];
          outboundShipment.shipmentLineItems.items.forEach((lineItem) => {
            lineItem.inventoryItems.items.forEach((inventoryItem) => {
              inventoryItem.inventory = lineItem.inventory;
              inventoryItems.push(inventoryItem);
            });
          });
          sortShipmentItemByLocation(inventoryItems, true);
          dispatch({
            type: "SET_CURRENT_SHIPPMENT",
            payload: { outboundShipment, inventoryItems },
          });
        }
      } catch (err) {
        logError("ProcessOutboundShipmentCard:getData", err, notify);
      } finally {
        if (showSpinner) {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, match.params.id, notify],
  ); // goToOutbound uses stable dispatch/history

  useEffect(() => {
    getData();

    return () => {
      dispatch({ type: "CLEAR_INVENTORY" });
    };
  }, [getData, dispatch]);

  const currentShipmentId = currentShipment?.id;
  const currentReloadYardId = currentShipment?.reloadYardId;

  useTrackCarSubscriptionRefresh({
    enabled: Boolean(currentReloadYardId),
    label: "ProcessShipmentsCard",
    notify,
    shouldRefresh: (trackCar) => {
      if (!trackCar?.reloadYardId) return false;
      if (trackCar.reloadYardId !== currentReloadYardId) return false;
      if (
        currentShipmentId &&
        trackCar?.shipmentId &&
        trackCar.shipmentId !== currentShipmentId
      ) {
        return false;
      }
      return true;
    },
    onRefresh: (trackCar, eventName) => {
      if (
        eventName === "update" &&
        trackCar?.shipmentId === currentShipmentId &&
        trackCar?.trackId
      ) {
        dispatch({
          type: "UPDATE_SHIPMENT_LOCATION",
          payload: {
            shipmentId: trackCar.shipmentId,
            trackId: trackCar.trackId,
            locationFullName: trackCar.locationFullName,
          },
        });
      }
      getData({ showSpinner: false });
    },
  });

  useEffect(() => {
    if (isComponentMounted && currentShipment.carrierSignatureKey)
      getSignature(currentShipment.carrierSignatureKey, setCarrierSignatureImg);

    return () => (isComponentMounted.current = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShipment.carrierSignatureKey]);

  useEffect(() => {
    if (isComponentMounted && currentShipment.yardSignatureKey)
      getSignature(currentShipment.yardSignatureKey, setYardSignatureImg);
    return () => (isComponentMounted.current = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShipment.yardSignatureKey]);

  const getSignature = async (key, fn) => {
    if (!key) return;
    try {
      let data = await Storage.get(key, { download: true });
      processS3Sig(data.Body, fn);
      debugger;
    } catch (err) {
      logError("OutboundShipmentCard::getSignature", err, notify);
    }
  };

  const processS3Sig = (blob, fn) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      const base64data = reader.result;
      fn(base64data);
    };
  };

  const saveShipment = async (toUpdateShipment) => {
    debugger;
    toUpdateShipment = { ...toUpdateShipment };
    if (!toUpdateShipment.startDateTime) {
      toUpdateShipment.startDateTime = new Date().toISOString();
    }

    const promiseList = [];
    toUpdateShipment.shipmentLineItems.items.forEach((shipmentLineItem) => {
      const toUpdateShipmentItem = {
        id: shipmentLineItem.id,
        inventoryId: shipmentLineItem.inventoryId,
        outboundShipmentId: shipmentLineItem.outboundShipmentId,
        sortKey: shipmentLineItem.sortKey,
      };
      if (
        shipmentLineItem.inventoryItems.items.some(
          (itm) => itm.status === ShipmentLineItemStatus.REJECTED,
        )
      ) {
        toUpdateShipmentItem.status = ShipmentLineItemStatus.REJECTED;
      } else if (
        shipmentLineItem.inventoryItems.items.every(
          (itm) => itm.status === ShipmentLineItemStatus.LOADED,
        )
      ) {
        toUpdateShipmentItem.status = ShipmentLineItemStatus.LOADED;
      } else if (
        shipmentLineItem.inventoryItems.items.every(
          (itm) => itm.status === ShipmentLineItemStatus.VERIFIED,
        )
      ) {
        toUpdateShipmentItem.status = ShipmentLineItemStatus.VERIFIED;
      } else {
        toUpdateShipmentItem.status = ShipmentLineItemStatus.PENDING;
      }
      promiseList.push(
        API.graphql(
          graphqlOperation(updateShipmentLineItemMini, {
            input: toUpdateShipmentItem,
          }),
        ),
      );
    });

    promiseList.splice(0, 0, performUpdateOutbound(toUpdateShipment));
    const response = await Promise.all(promiseList);
    debugger;
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: response[0].data.updateOutboundShipment,
    });
    return response[0].data.updateOutboundShipment;
  };

  const updateShipmentHistory = async (toUpdateShipment) => {
    toUpdateShipment = { ...toUpdateShipment };
    let response = await performUpdateOutbound(toUpdateShipment);
    debugger;
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: response.data.updateOutboundShipment,
    });
    debugger;
  };

  const validateForm = () => {
    clearForm();
    let valid = true;
    debugger;

    if (
      currentShipment.status === OutboundStatus.ARRIVED ||
      currentShipment.status === OutboundStatus.PLACED
    ) {
      valid = inventoryItems.reduce(
        (valid, itm) =>
          valid &&
          (itm.status === ShipmentLineItemStatus.LOADED ||
            itm.status === ShipmentLineItemStatus.VERIFIED ||
            itm.status === ShipmentLineItemStatus.REJECTED),
        valid,
      );
      if (!valid) {
        notify(
          `Complete all line items before completing the outbound shipment.`,
          "warning",
        );
      }
      if (valid && isPickListVerified()) valid = validateSignature();
    } else if (currentShipment.status === OutboundStatus.LOADED) {
      if (!hasRejectedItems()) {
        valid = isPickListVerified();
        if (!valid) {
          notify(
            `Verify all line items before completing the outbound shipment.`,
            "warning",
          );
        }

        if (valid) valid = validateSignature();
      }
    }
    return valid;
  };

  const validateSignature = () => {
    let valid = true;
    if (!currentShipment.skipSignature) {
      if (!carrierSignatureImg || !yardSignatureImg) {
        valid = false;
        notify(`Signatures required.`, "warning");
      }
    }
    return valid;
  };

  const validatePhotos = (previousStatus, previousState) => {
    let valid = true;
    debugger;
    if (
      currentShipment.state === OutboundState.COMPLETED &&
      currentShipment.account?.outboundPhotos > 0
    ) {
      const requiredPhotos =
        Number(currentShipment.account.outboundPhotos) || 0;
      const completedPhotoCount = getCompletedPhotoCount();
      if (!photos || completedPhotoCount < requiredPhotos) {
        valid = false;
        setPhotosErrorMsg(`${requiredPhotos} photos are required to complete.`);
        currentShipment.status = previousStatus;
        currentShipment.state = previousState;
        notify(`${requiredPhotos} photos are required.`, "warning");
      } else {
        setPhotosErrorMsg("");
      }
    }
    return valid;
  };

  const clearForm = () => {
    setPhotosErrorMsg("");
  };

  const handleSave = async ({ skipSealsValidation }) => {
    try {
      if (shouldRedirectToTrackManagement(currentShipment)) {
        redirectToTrackManagement();
        return;
      }
      if (
        currentShipment?.account?.trackOutboundSeals &&
        currentShipment?.seals?.items.length === 0 &&
        !skipSealsValidation
      ) {
        setIsSealsConfirmationOpen(true);
        return false;
      }
      debugger;
      setSaving(true);
      const previousStatus = currentShipment.status;
      const previousState = currentShipment.state;

      if (!validateForm()) return;
      setNextState(currentShipment);
      if (!validatePhotos(previousStatus, previousState)) return;
      if (note) {
        const newNote = {
          date: new Date(),
          type: NoteType.GENERIC,
          by: window.appUser?.fullName,
          text: note,
        };
        if (currentShipment.notes) currentShipment.notes.push(newNote);
        else currentShipment.notes = [newNote];
      }
      if (
        previousStatus !== currentShipment.status ||
        previousState !== currentShipment.state
      ) {
        const newHistory = {
          type: NoteType.SHIPMENT,
          date: new Date(),
          by: window.appUser?.fullName,
          text: `Shipment saved | status: ${
            currentShipment.status
          } ${getLogState(currentShipment.state)}`,
        };
        if (currentShipment.history) currentShipment.history.push(newHistory);
        else currentShipment.history = [newHistory];
      }

      const updatedShipment = await saveShipment(currentShipment);
      dispatch({ type: "CLEAR_NOTE", payload: updatedShipment });

      history && goToOutbound();
    } catch (err) {
      logError(
        "ProcessShipmentsCard:OutboundShipmentCard:handleSave",
        err,
        notify,
        redirect,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSealsConfirmationClose = () => {
    setIsSealsConfirmationOpen(false);
  };

  const handleSealsConfirmationConfirm = () => {
    setIsSealsConfirmationOpen(false);
    handleSave({ skipSealsValidation: true });
  };

  const shouldRedirectToTrackManagement = needsOutboundRailcarSpotting;

  const redirectToTrackManagement = () => {
    history.push({
      pathname: "/trackManagement",
      state: {
        defaultShipmentId: currentShipment.id,
        returnPath: match.url,
      },
    });
  };

  const goToOutbound = () => {
    dispatch({
      type: "CLEAR_INVENTORY",
      payload: {
        currentInventory: {},
        selectedInventoryItem: [],
      },
    });
    history.push("/processOutboundShipments");
  };

  const setNextState = (currentShipment) => {
    debugger;
    if (
      currentShipment.status === OutboundStatus.ARRIVED ||
      currentShipment.status === OutboundStatus.PLACED
    ) {
      if (isPickListVerified()) {
        currentShipment.state = OutboundState.COMPLETED;
        currentShipment.endDateTime = new Date().toISOString();
      }
      currentShipment.status = OutboundStatus.LOADED;
    } else if (currentShipment.status === OutboundStatus.LOADED) {
      if (hasRejectedItems()) {
        applyRejectionRollback(currentShipment);
      } else {
        currentShipment.endDateTime = new Date().toISOString();
        currentShipment.state = OutboundState.COMPLETED;
        currentShipment.status = OutboundStatus.LOADED;
      }
    }
  };

  const getSubmitButtonText = () => {
    // Same precedence as isSubmitButtonDisabled — otherwise a completed
    // unspotted railcar renders a disabled button labelled "Spot Railcar".
    if (
      currentShipment.state !== OutboundState.COMPLETED &&
      shouldRedirectToTrackManagement(currentShipment)
    ) {
      return "Spot Railcar";
    }
    if (window.appUser.type !== UserType.CREW && hasRejectedItems()) {
      return "Reject";
    }
    return "Complete";
  };

  const isSubmitButtonDisabled = () => {
    if (saving) return true;
    // Completed wins over every other reason to enable the button.
    if (currentShipment.state === OutboundState.COMPLETED) return true;

    if (shouldRedirectToTrackManagement(currentShipment)) return false;

    if (inventoryItems?.length === 0) return true;
    const hasPending = inventoryItems?.some(
      (itm) => itm.status === ShipmentLineItemStatus.PENDING,
    );
    if (hasPending) return true;
    return false;
  };

  const isScanSealDisabled = () => {
    if (saving) return true;
    return inventoryItems?.some(
      (itm) => itm.status === ShipmentLineItemStatus.PENDING,
    );
  };

  const hasRejectedItems = () => {
    let hasRejected = false;
    inventoryItems?.forEach((itm) => {
      if (itm.status === ShipmentLineItemStatus.REJECTED) hasRejected = true;
    });
    return hasRejected;
  };

  function handleCancel() {
    goToOutbound();
  }

  const isUploadEnabled = () => {
    return currentShipment.state !== OutboundState.COMPLETED;
  };

  const isPickListVerified = () => {
    let valid = inventoryItems.reduce(
      (valid, itm) => valid && itm.status === ShipmentLineItemStatus.VERIFIED,
      true,
    );
    return valid;
  };

  const handleClickOpen = (sigType) => {
    debugger;
    setSignatureType(sigType);
    setInvalidSigMsg("");
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const clearSignature = () => {
    setInvalidSigMsg("");
  };

  const addSignature = async ({ signatureBinaryImage, signatureDataUrl }) => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setInvalidSigMsg("Invalid signature");
      return;
    }
    try {
      const key = generateSignatureKey(window.appUser?.reloadYardId);
      await Storage.put(key, signatureBinaryImage, {
        level: "public",
        contentType: "image/png",
      });
      currentShipment[signatureType] = key;
      currentShipment.signedBy = window.appUser.fullName;
      await saveShipment({ ...currentShipment });

      setOpen(false);
      if (signatureType === "carrierSignatureKey") {
        dispatch({ type: "UPDATE_CARRIER_IMG", payload: signatureDataUrl });
      } else {
        dispatch({ type: "UPDATE_YARD_IMG", payload: signatureDataUrl });
      }
    } catch (err) {
      logError(
        "ProcessShipmentsCard:OutboundShipmentCard:addSignature",
        err,
        notify,
      );
    }
  };

  const setShipmentNote = (value) => {
    dispatch({
      type: "SET_SHIPPMENT_NOTE",
      payload: { id: currentShipment.id, value },
    });
  };

  const skipSignature = () => {
    debugger;
    dispatch({
      type: "UPDATE_CURRENT_SHIPMENT",
      payload: {
        ...currentShipment,
        skipSignature: !currentShipment.skipSignature,
      },
    });
  };

  const getShipmentIcon = () => {
    if (currentShipment.carrierType === CarrierType.RAIL)
      return <TrainIcon fontSize="large" />;
    return <TruckIcon fontSize="large" />;
  };

  const getNetWeight = () => {
    debugger;
    if (!currentShipment.netWeight) return <></>;
    const value = defaultNumberFormat.format(currentShipment.netWeight);

    return (
      currentShipment.carrierType === CarrierType.TRUCK && (
        <Grid item sm={6} xs={12}>
          <Typography>
            {`Net weight: ${value} ${currentShipment.weightUnits}`}
          </Typography>
        </Grid>
      )
    );
  };

  if (invalidIdError) return <ErrorCard error={invalidIdError} />;

  return (
    <CrewCard
      title={
        currentShipment.transfer
          ? "Transfer"
          : currentShipment.pickupNumber || currentShipment.railCarNumber
      }
      icon={getShipmentIcon()}
      subheader={
        currentShipment.transfer
          ? currentShipment.transferToAccountName
          : currentShipment.carrier
      }
      action={
        <IconButton edge="start" onClick={goToOutbound}>
          <HighlightOffIcon fontSize="large" />
        </IconButton>
      }
      showDivider={false}
    >
      <CardContent>
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Grid container spacing={2}>
          <Grid item sm={12} xs={12}>
            <PhotoManager
              parentId={currentShipment.id}
              parentType={ResourceType.OUTBOUND}
              parentLabel={
                currentShipment?.railCarNumber ||
                currentShipment?.pickupNumber ||
                currentShipment?.poNumber
              }
              disabled={!isUploadEnabled()}
              viewOnly={false}
              minPhotosNum={
                // photo required on inspect only, after loaded
                currentShipment?.status === OutboundStatus.LOADED
                  ? currentShipment.account?.outboundPhotos
                  : 0
              }
              maxPhotosNum={MAX_PHOTO_NUM_OUTBOUND}
              updateParentFn={setPhotos}
            />
            <div className={classes.photoErrorSection}>
              {photosErrorMsg && (
                <Typography variant="caption" display="block" color="error">
                  {photosErrorMsg}
                </Typography>
              )}
            </div>
          </Grid>
          {!currentShipment?.skipSignature &&
            isAuthorized(AclType.OUTBOUND_SIGN) && (
              <Grid container spacing={2}>
                <Grid item>
                  <div
                    style={{
                      marginLeft: 4,
                      marginBottom: 8,
                      marginTop: 8,
                    }}
                  >
                    <Button
                      onClick={() => handleClickOpen("yardSignatureKey")}
                      variant="outlined"
                      size="large"
                      disabled={!isUploadEnabled()}
                      endIcon={
                        currentShipment.yardSignatureKey ? (
                          <CheckIcon fontSize="large" />
                        ) : (
                          <ClearIcon fontSize="large" />
                        )
                      }
                    >
                      {isSmall ? "Yard Sig" : "Yard Signature"}
                    </Button>
                  </div>
                </Grid>
                <Grid item>
                  <div
                    style={{
                      marginBottom: 8,
                      marginTop: 8,
                    }}
                  >
                    <Button
                      onClick={() => handleClickOpen("carrierSignatureKey")}
                      variant="outlined"
                      size="large"
                      disabled={!isUploadEnabled()}
                      endIcon={
                        currentShipment.carrierSignatureKey ? (
                          <CheckIcon fontSize="large" />
                        ) : (
                          <ClearIcon fontSize="large" />
                        )
                      }
                    >
                      {isSmall ? "Carrier Sig" : "Carrier Signature"}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            )}
          {(!carrierSignatureImg || !yardSignatureImg) &&
            isAuthorized(AclType.OUTBOUND_SIGN) && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentShipment.skipSignature ? true : false}
                      onChange={() => skipSignature()}
                    />
                  }
                  label="Skip e-signatures"
                />
              </Grid>
            )}
          {/* <Grid item sm={6} xs={12}>
              {getChecklist()}
            </Grid> */}

          <Grid item sm={6} xs={12}>
            {currentShipment.carrierType === CarrierType.RAIL ? (
              <Typography>
                Railcar number: {currentShipment.railCarNumber}
              </Typography>
            ) : (
              <Typography>
                Trailer number: {currentShipment.railCarNumber}
              </Typography>
            )}
          </Grid>
          {!currentShipment.transfer &&
            currentShipment.carrierType === CarrierType.TRUCK && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  Pickup number: {currentShipment.pickupNumber}
                </Typography>
              </Grid>
            )}
          <Grid item sm={6} xs={12}>
            <Typography>PO number: {currentShipment.poNumber}</Typography>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Typography noWrap={true}>
              Account: {accountNameRenderer(currentShipment)}
            </Typography>
          </Grid>
          {!currentShipment.transfer &&
            currentShipment.carrierType === CarrierType.TRUCK && (
              <Grid item sm={6} xs={12}>
                <Typography>
                  Must Tarp: {currentShipment.mustTarp ? "Yes" : "No"}
                </Typography>
              </Grid>
            )}
          {getNetWeight()}
          {currentShipment.customFields && (
            <Grid item xs={12}>
              <CustomFieldsMobile
                recordFields={currentShipment.customFields}
                accountFields={currentShipment.account?.outboundCustomFields}
                width={6}
              />
            </Grid>
          )}
          {currentShipment?.account?.trackOutboundSeals && (
            <>
              <Grid item xs={12}>
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={<ScannerIcon />}
                  disabled={isScanSealDisabled()}
                  onClick={() => setIsSealDialogOpen(true)}
                >
                  Scan Seals
                </Button>
              </Grid>
              {isSealDialogOpen && (
                <SealDialog
                  saveShipment={updateShipmentHistory}
                  setIsSealDialogOpen={setIsSealDialogOpen}
                />
              )}
            </>
          )}
          <Grid item xs={12}>
            <TextField
              name="notes"
              id="outlined-multiline-static"
              label="Notes"
              multiline
              minRows={3}
              disabled={!isUploadEnabled()}
              margin="dense"
              variant="outlined"
              fullWidth
              value={note}
              onChange={(evt) => setShipmentNote(evt.target.value)}
            />
          </Grid>
          {currentShipment.instructions && (
            <Grid item xs={12}>
              <Typography>
                Instructinos: {currentShipment.instructions}
              </Typography>
            </Grid>
          )}
        </Grid>
        <>
          {currentShipment.shipmentLineItems ? (
            <InventoryList
              classes={classes}
              currentShipment={currentShipment}
              inventoryItems={inventoryItems}
              history={history}
              match={match}
              pickListDisabled={shouldRedirectToTrackManagement(
                currentShipment,
              )}
            />
          ) : (
            <Typography className={classes.notFound}>
              No shipment items found.
            </Typography>
          )}
        </>
        {open && (
          <SignatureDialog
            open={open}
            handleClose={handleClose}
            saving={saving}
            signatureRef={signatureRef}
            signatureImg={
              signatureType === "carrierSignatureKey"
                ? carrierSignatureImg
                : yardSignatureImg
            }
            addSignature={addSignature}
            invalidSignatureMessage={invalidSigMsg}
            clearSignature={clearSignature}
          />
        )}
        <SealsConfirmationDialog
          open={isSealsConfirmationOpen}
          onClose={handleSealsConfirmationClose}
          onConfirm={handleSealsConfirmationConfirm}
        />
      </CardContent>
      <CardActions className={classes.actions}>
        <Button size="large" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          size="large"
          disabled={isSubmitButtonDisabled()}
          onClick={handleSave}
        >
          {getSubmitButtonText()}
        </Button>
      </CardActions>
    </CrewCard>
  );
}

const InventoryList = ({
  classes,
  currentShipment,
  inventoryItems,
  history,
  pickListDisabled,
}) => {
  const { dispatch } = useOutboundContext();
  const [showAll, setShowAll] = useStickyState(false, currentShipment.id);

  const getIcon = (lineItm) => {
    if (lineItm.status === ShipmentLineItemStatus.LOADED)
      return <DoneIcon fontSize="inherit" />;
    else if (lineItm.status === ShipmentLineItemStatus.VERIFIED)
      return <DoubleCheckIcon fontSize="inherit" />;
    return <CloseOutlinedIcon fontSize="inherit" />;
  };

  const getAvatarClassName = (lineItm) => {
    if (lineItm.hasException) {
      return classes.amber;
    }
    if (
      lineItm.status === ShipmentLineItemStatus.LOADED ||
      lineItm.status === ShipmentLineItemStatus.VERIFIED
    )
      return classes.green;
    else if (lineItm.status === ShipmentLineItemStatus.REJECTED)
      return classes.orange;
    return classes.unprocessedItem;
  };

  const handleSelect = (lineItm) => {
    if (pickListDisabled) return;
    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({
      type: "SET_CURRENT_SHIPMENT_LINE_ITEM",
      payload: { currentLineItem: lineItm },
    });
  };

  const getListDisplay = (currentShipment, lineItm) => {
    const primaryField = getInventoryPrimaryField(
      currentShipment.account,
      lineItm.inventory,
      lineItm,
    );
    return (
      primaryField.label +
      ": " +
      primaryField.value +
      " - Qty: " +
      lineItm.quantityToPick +
      " " +
      getUnitName(currentShipment.totalUOM)
    );
  };

  const getViewInventoryItems = () => {
    if (showAll) return inventoryItems;
    if (currentShipment.status !== OutboundStatus.LOADED) {
      return inventoryItems.filter(
        (itm) => itm.status !== ShipmentLineItemStatus.LOADED,
      );
    }
    return inventoryItems.filter(
      (itm) => itm.status !== ShipmentLineItemStatus.VERIFIED,
    );
  };

  return (
    <div className={classes.inventoriesSection}>
      <Grid container>
        <Grid item xs={6}>
          <Box mt={0.15} display="flex" alignItems="center">
            <Typography variant="h6" color="textSecondary">
              Pick List
            </Typography>
            {pickListDisabled && (
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginLeft: 8 }}
              >
                (Spot railcar first)
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box display="flex" alignItems="center" justifyContent="flex-end">
            <FormControlLabel
              control={
                <Switch
                  checked={showAll}
                  onChange={() => setShowAll(!showAll)}
                  name="show-loaded"
                  disabled={pickListDisabled}
                />
              }
              label="Show all"
            />
          </Box>
        </Grid>
      </Grid>
      <List>
        {getViewInventoryItems().map((lineItm) => {
          const listItem = (
            <ListItem
              onClick={() => handleSelect(lineItm)}
              button={!pickListDisabled}
              disabled={pickListDisabled}
            >
              <ListItemAvatar>
                <Avatar className={getAvatarClassName(lineItm)}>
                  {getIcon(lineItm)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography>{lineItm.inventory.productName}</Typography>
                }
                secondary={
                  <>
                    <Typography variant="subtitle2" color="textSecondary">
                      Location:{" "}
                      {currentShipment?.trackCar?.locationFullName ??
                        lineItm?.inventoryItem?.locationFullName ??
                        ""}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                      {getListDisplay(currentShipment, lineItm)}
                    </Typography>
                  </>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleSelect(lineItm)}
                  disabled={pickListDisabled}
                >
                  <KeyboardArrowRightIcon fontSize="large" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
          return (
            <div key={lineItm.id}>
              {pickListDisabled ? (
                listItem
              ) : (
                <Link
                  className={classes.link}
                  to={history.location.pathname + "/inventories/" + lineItm.id}
                >
                  {listItem}
                </Link>
              )}
              <Divider />
            </div>
          );
        })}
      </List>
    </div>
  );
};

const SealsConfirmationDialog = ({ open, onClose, onConfirm }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Seals Confirmation</DialogTitle>
      <DialogContent>
        <Typography>
          No seals have been scanned for this outbound. Are you sure you want to
          proceed without adding seals?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="primary">
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};
